import React from 'react';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import { getLocationSalesDataQuery } from './queries';
import OrderStatsSection from '../../../../../components/OrderStatsSection';

interface Props {
  locationId: string;
  dateRange: { startDate: Date | null; endDate: Date | null };
  customDateValid: boolean;
  classes: any;
}

const ServedOrderSection: React.FC<Props> = ({ locationId, dateRange, customDateValid, classes }) => {
  const { data, loading } = useQuery(
    getLocationSalesDataQuery(
      locationId,
      dateRange.startDate ? moment(dateRange.startDate).startOf('day').format('YYYY-MM-DDTHH:mm:ss') : '',
      dateRange.endDate ? moment(dateRange.endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss') : ''
    ),
    {
      fetchPolicy: 'no-cache',
      skip: !locationId || !customDateValid,
    }
  );

  const orders = data?.locations_by_pk?.orders_served || [];
  const aggregate = data?.locations_by_pk?.orders_served_aggregate?.aggregate;

  const salesByDate = orders.reduce((acc: Record<string, number>, order: any) => {
    const date = moment(order.created_at).format('YYYY-MM-DD');
    if (!acc[date]) acc[date] = 0;
    acc[date] += order.total || 0;
    return acc;
  }, {} as Record<string, number>);
  const ordersCountByDate = orders.reduce((acc: Record<string, number>, order: any) => {
    const date = moment(order.created_at).format('YYYY-MM-DD');
    if (!acc[date]) acc[date] = 0;
    acc[date] += 1;
    return acc;
  }, {} as Record<string, number>);
  const coversDist: Record<string, number> = {};
  orders.forEach((order: any) => {
    const covers = order.covers || 0;
    coversDist[covers] = (coversDist[covers] || 0) + 1;
  });
  let quickSale = 0,
    dineIn = 0;
  orders.forEach((order: any) => {
    if (order.quick_sale) quickSale++;
    else dineIn++;
  });

  const metrics = {
    totalSales: aggregate?.sum?.total || 0,
    totalOrders: aggregate?.count || 0,
    avgOrderValue: aggregate?.count > 0 ? aggregate?.sum?.total / aggregate?.count : 0,
  };

  const tableColumns = [
    { key: '#', label: '#', render: (_: any, idx: number) => idx + 1 },
    { key: 'order_number', label: 'Order Number' },
    { key: 'created_at', label: 'Date', render: (order: any) => moment(order.created_at).format('YYYY-MM-DD HH:mm') },
    { key: 'total', label: 'Total', render: (order: any) => `£${(order.total / 100).toFixed(2)}` },
    { key: 'covers', label: 'Covers', render: (order: any) => (order.covers !== undefined ? order.covers : '-') },
    { key: 'table_number', label: 'Table', render: (order: any) => (order.table_number !== undefined ? order.table_number : '-') },
    { key: 'quick_sale', label: 'Quick Sale', render: (order: any) => (order.quick_sale !== undefined ? (order.quick_sale ? 'Yes' : 'No') : '-') },
    { key: 'status', label: 'Status', render: (order: any) => order.status || '-' },
  ];

  return (
    <OrderStatsSection
      loading={loading}
      metrics={metrics}
      salesByDate={salesByDate}
      ordersCountByDate={ordersCountByDate}
      coversDist={coversDist}
      quickSale={quickSale}
      dineIn={dineIn}
      orders={orders}
      tableColumns={tableColumns}
      classes={classes}
    />
  );
};

export default ServedOrderSection;
